import { flowRight, get, isEmpty, toNumber } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { focus } from 'redux-form';
import { COMMENTS_PER_PAGE } from '../../constants/pagination';
import { fetchComments } from '../../actions/fetch-comments';
import { fetchPost } from '../../actions/fetch-post';
import { fetchPostAndRedirect } from '../../actions/fetch-post-and-redirect';
import { incrementCommentLikeCount } from '../../actions/increment-comment-like-count';
import { incrementPostViewCount } from '../../actions/increment-post-view-count';
import { pageOpened } from '../../actions/page-opened';
import { emitOpenPost, emitClosePost } from '../../actions/post-socket';
import { redirectTo404 } from '../../actions/redirect-to-404';
import { setIsLoaded } from '../../actions/set-is-loaded';
import AppLoaded from '../../components/app-loaded';
import AccountSuspended from '../../components/account-suspended';
import AnimatedLoader from '../../components/animated-loader';
import Button from '../../components/button';
import CommentingDisabled from '../../components/commenting-disabled';
import EmptyComment from '../../components/empty-comment';
import EmptyStates from '../../components/empty-states';
import Link from '../../components/link/internal-link';
import PageMetadata from '../../components/page-metadata';
import Pagination from '../../components/pagination-comments';
import PostContent from '../../components/post-content';
import PostHeader from '../../components/post-header';
import PostPageLayout from '../../components/post-page-layout';
import PostPageStats from '../../components/post-page-stats';
import PostTitle from '../../components/post-title';
import TimeAgo from '../../components/time-ago';
import PostCommentList from '../../containers/post-comment-list';
import PostMainActions from '../../containers/post-main-actions';
import PostPageFooter from '../../containers/post-page-footer';
import contentStateToString from '../../services/content-state-to-string';
import { POST_PAGE } from '../../services/detect-route';
import getPostCover from '../../services/get-post-cover';
import hashLinkHandler from '../../services/hash-link-handler';
import { getLastPage } from '../../services/pagination';
import { push, replace } from '../../services/router-utils';
import { scrollToAppTop } from '../../services/scroll-to';
import { getCategoryBySlug } from '../../selectors/categories-selectors';
import { getCommentsByPostId } from '../../selectors/comment-selectors';
import { getCurrentUserId } from '../../selectors/current-user-selectors';
import { getIsPostLoaded, getIsCommentsLoaded } from '../../selectors/is-loaded-selectors';
import { getIsPostLoading, getIsCommentsLoading } from '../../selectors/is-loading-selectors';
import { getEntityCount } from '../../selectors/pagination-selectors';
import { getPostBySlug } from '../../selectors/post-selectors';
import withAuth from '../../hoc/with-auth';
import withCardBorderWidth from '../../hoc/with-card-border-width';
import withDeviceType from '../../hoc/with-device-type';
import withFontClassName from '../../hoc/with-font-class-name';
import withTranslate from '../../hoc/with-translate';
import styles from './post-page.scss';

export class PostPage extends Component {
  static fetchData = ({ store, params: { categorySlug, postSlug }, query, search }) =>
    store
      .dispatch(fetchPostAndRedirect(categorySlug, postSlug, search))
      .then(post =>
        store.dispatch(
          fetchComments({
            postId: post._id,
            postSlug,
            page: toNumber(query.page) || 1,
            pageSize: COMMENTS_PER_PAGE,
          })
        )
      )
      .catch(error => {
        if (error.status === 401) {
          return store.dispatch(
            push({
              pathname: `/login${search}&redirect=/${categorySlug}/${postSlug}`,
            })
          );
        } else {
          return store.dispatch(redirectTo404(search));
        }
      });

  state = {};

  componentDidMount = () => {
    const { params: { postSlug, categorySlug }, isAuthenticated, category, push, replace } = this.props;

    if (isEmpty(category)) {
      return;
    }

    if (!isAuthenticated && category.isPrivate) {
      replace(`/login?redirect=/${categorySlug}/${postSlug}`);
    } else if (isAuthenticated || !category.isPrivate) {
      this.props
        .fetchPost(postSlug)
        .then(post => {
          this.fetchComments(post, postSlug);
          this.props.incrementPostViewCount(post._id);
          this.props.emitOpenPost(post);
          this.props.pageOpened({
            page: POST_PAGE,
            post,
          });
        })
        .catch(error => {
          if (error.status === 401) {
            push(`/${categorySlug}`);
          }
        });
    }
  };

  componentWillReceiveProps = nextProps => {
    const { params: { postSlug, categorySlug }, isAuthenticated, category, push, replace } = nextProps;

    if (isEmpty(category)) {
      return;
    }

    if (!isAuthenticated && category.isPrivate) {
      replace(`/login?redirect=/${categorySlug}/${postSlug}`);
    } else if ((isAuthenticated || !category.isPrivate) && this.props.params.postSlug !== postSlug) {
      this.props
        .fetchPost(postSlug)
        .then(post => {
          this.fetchComments(post, postSlug);
          this.props.incrementPostViewCount(post._id);
          this.props.emitOpenPost(post);
          this.props.pageOpened({
            page: POST_PAGE,
            post,
          });
        })
        .catch(error => {
          if (error.status === 401) {
            push(`/${categorySlug}`);
          }
        });
    }
  };

  fetchComments = (post, postSlug, page = this.props.page) => {
    return this.props.fetchComments({
      postId: post._id,
      postSlug,
      pageSize: COMMENTS_PER_PAGE,
      page,
    });
  };

  componentWillUnmount = () => {
    this.props.setIsLoaded('comments', this.props.params.postSlug, false);
    if (!isEmpty(this.props.category)) {
      this.props.emitClosePost(this.props.post);
    }
  };

  componentDidUpdate = () => {
    hashLinkHandler(this.props.location);
  };

  handleEmptyCommentMouseDown = () => {
    if (document.activeElement.tagName.toLowerCase() === 'textarea') {
      this.ignoreClick = true;
    }
  };

  handleEmptyCommentClick = () => {
    if (!this.ignoreClick) {
      this.props.focusCommentForm();
    }
    delete this.ignoreClick;
  };

  renderEmptyComment = () => {
    if (!this.props.allComments.length && !this.props.post.isCommentsDisabled) {
      return <EmptyComment onClick={this.handleEmptyCommentClick} onMouseDown={this.handleEmptyCommentMouseDown}/>;
    }
  };

  setFirstCommentBottomPosition = firstCommentBottomPosition => {
    this.setState({ firstCommentBottomPosition });
  };

  renderPagination = () => {
    const { page, entityCount, isMobile, isCommentsLoaded } = this.props;
    if (!isMobile && isCommentsLoaded) {
      return (
        <Pagination
          page={page}
          entityCount={entityCount}
          showPerPage={COMMENTS_PER_PAGE}
          onChange={this.handlePageChange}
        />
      );
    }
  };

  handleCommentCreate = () => {
    const { post, entityCount, params: { postSlug, categorySlug } } = this.props;
    const lastPage = getLastPage(entityCount, COMMENTS_PER_PAGE);
    if (this.props.page === lastPage) {
      return;
    }

    this.fetchComments(post, postSlug, lastPage);
    this.props.push({
      pathname: `/${categorySlug}/${postSlug}`,
      query: { page: lastPage },
    });
  };

  handlePageChange = ({ page }) => {
    const { post, params: { postSlug, categorySlug } } = this.props;
    this.fetchComments(post, postSlug, page).then(scrollToAppTop);
    this.props.push({
      pathname: `/${categorySlug}/${postSlug}`,
      query: { page },
    });
  };

  loadMore = page => {
    const { post, params: { postSlug } } = this.props;
    this.fetchComments(post, postSlug, page);
  };

  renderEditDate = () => {
    const { post, t } = this.props;

    if (post.editedDate) {
      return (
        <p className={styles.edited}>
          {t('post-page.edited')}&nbsp;<span className={styles.editedDate}>
            <TimeAgo time={post.editedDate}/>
          </span>
        </p>
      );
    }
  };

  renderFooter = () => {
    const { isBlocked, post, t, allComments, isCommentsLoading, isCommentsLoaded } = this.props;
    if (isBlocked || post.isCommentsDisabled) {
      return (
        <CommentingDisabled message={t(isBlocked ? 'post-page.commenting-blocked' : 'post-page.commenting-disabled')}/>
      );
    }

    const showFooter = (!isCommentsLoading && isCommentsLoaded) || !isEmpty(allComments);
    if (!showFooter) {
      return null;
    }

    return (
      <PostPageFooter
        onCommentCreate={this.handleCommentCreate}
        postId={post._id}
        isSticky={Boolean(this.state.firstCommentBottomPosition && allComments.length)}
        stickyThreshold={this.state.firstCommentBottomPosition}
      />
    );
  };

  renderPost = () => {
    const { category, post, location, borderWidth, titleFontClassName, page } = this.props;
    if (page > 1) {
      return null;
    }

    const postContainerClassName = classNames(
      styles.postContainer,
      'forum-card-background-color',
      'forum-card-border-color',
      'forum-text-color'
    );

    const isHeaderAnimated = location.state && location.state.isAnimated;
    return (
      <main className={postContainerClassName} style={{ borderWidth }}>
        <div className={styles.postContentWrapper} data-hook="post-page__post">
          <PostHeader type={PostHeader.SINGLE} post={post} isAnimated={isHeaderAnimated}/>
          <PostTitle className={titleFontClassName} type={PostTitle.SINGLE} title={post.title || ''} component="h1"/>
          <div className={styles.postStatsContainer}>
            <PostPageStats viewCount={post.viewCount} commentCount={post.totalComments} isAnimated={isHeaderAnimated}/>
            {this.renderEditDate()}
          </div>
          <div className={styles.postContent}>
            <PostContent post={post}/>
          </div>
          <PostMainActions category={category} post={post}/>
        </div>
      </main>
    );
  };

  renderEmpty = () => {
    const { t } = this.props;
    return (
      <EmptyStates
        className={styles.emptyState}
        title={t('post-page.not-found-title')}
        content={t('post-page.not-fount-content')}
      >
        <Button component={Link} to="/">
          {t('post-page.back-to-forum')}
        </Button>
      </EmptyStates>
    );
  };

  render = () => {
    const {
      params: { postSlug },
      isBlocked,
      category,
      post,
      contentFontClassName,
      isLoading,
      isLoaded,
      isCommentsLoading,
      isCommentsLoaded,
      allComments,
    } = this.props;

    if (isEmpty(post) && !isLoading) {
      return this.renderEmpty();
    }

    if (!isEmpty(category) && category.isPrivate && isBlocked) {
      return <AccountSuspended className={styles.emptyState}/>;
    }

    const showCommentsLoader = isCommentsLoading && !isCommentsLoaded && isEmpty(allComments);

    return (
      <PostPageLayout className={classNames(styles.postPage, contentFontClassName, 'forum-background-color')}>
        <PageMetadata
          title={post.title}
          description={contentStateToString(post.content)}
          image={getPostCover(post.content)}
          type="article"
        />
        <AnimatedLoader isLoading={isLoading && !isLoaded && isEmpty(post)}>
          {this.renderPost()}
          <AnimatedLoader isLoading={showCommentsLoader} isAnimated>
            <div>
              {this.renderEmptyComment()}
              <PostCommentList
                loadMore={this.loadMore}
                page={this.props.page}
                setFirstCommentBottomPosition={this.setFirstCommentBottomPosition}
                postSlug={postSlug}
              />
              {this.renderFooter()}
              {this.renderPagination()}
            </div>
          </AnimatedLoader>
        </AnimatedLoader>
        <AppLoaded parent={this} selector="[data-hook=post-page__post]"/>
      </PostPageLayout>
    );
  };
}

PostPage.propTypes = {
  fetchPost: PropTypes.func.isRequired,
  fetchComments: PropTypes.func.isRequired,
  incrementPostViewCount: PropTypes.func.isRequired,
  incrementCommentLikeCount: PropTypes.func.isRequired,
  category: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  allComments: PropTypes.array.isRequired,
  focusCommentForm: PropTypes.func.isRequired,
  location: PropTypes.object,
  page: PropTypes.number,
  isMobile: PropTypes.bool,
  emitOpenPost: PropTypes.func.isRequired,
  emitClosePost: PropTypes.func.isRequired,
  pageOpened: PropTypes.func.isRequired,
  t: PropTypes.func,
  typing: PropTypes.array,
  ownerId: PropTypes.string,
  entityCount: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  params: PropTypes.object,
  borderWidth: PropTypes.number.isRequired,
  titleFontClassName: PropTypes.string.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  setIsLoaded: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  isCommentsLoading: PropTypes.bool.isRequired,
  isCommentsLoaded: PropTypes.bool.isRequired,
  push: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const post = getPostBySlug(state, ownProps.params.postSlug) || {};
  const page = toNumber(get(ownProps, 'location.query.page', 1));
  return {
    category: getCategoryBySlug(state, ownProps.params.categorySlug) || {},
    post,
    allComments: getCommentsByPostId(state, post._id),
    typing: state.typing,
    ownerId: getCurrentUserId(state),
    isLoading: getIsPostLoading(state, ownProps.params.postSlug),
    isLoaded: getIsPostLoaded(state, ownProps.params.postSlug),
    isCommentsLoading: getIsCommentsLoading(state, ownProps.params.postSlug),
    isCommentsLoaded: getIsCommentsLoaded(state, ownProps.params.postSlug),
    entityCount: getEntityCount(state, 'comments'),
    page,
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      fetchPost,
      fetchComments,
      incrementPostViewCount,
      incrementCommentLikeCount,
      focusCommentForm: () => focus('comment', 'content'),
      emitOpenPost,
      emitClosePost,
      pageOpened,
      setIsLoaded,
      push,
      replace,
    },
    dispatch
  );

export default flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  withDeviceType,
  withFontClassName,
  withCardBorderWidth,
  withAuth,
  withTranslate
)(PostPage);



// WEBPACK FOOTER //
// ./app/containers/post-page/post-page.js